import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, Type, RotateCcw, ShieldCheck, Lock, Check } from 'lucide-react';
interface SignatureScreenProps {
  onNext: () => void;
  onBack: () => void;
}
type Method = 'draw' | 'type';
export function SignatureScreen({
  onNext,
  onBack
}: SignatureScreenProps) {
  const [method, setMethod] = useState<Method>('draw');
  const [hasSignature, setHasSignature] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<{
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    if (method !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#3B2F63';
    }
  }, [method]);
  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    lastPoint.current = getPos(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };
  const moveDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    const pos = getPos(e);
    if (ctx && lastPoint.current) {
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPoint.current = pos;
      if (!hasSignature) setHasSignature(true);
    }
  };
  const endDraw = () => {
    drawing.current = false;
    lastPoint.current = null;
  };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasSignature(false);
  };
  // Simulated auto-signature for quick demo flows
  const autoSign = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    ctx.moveTo(w * 0.18, h * 0.62);
    ctx.bezierCurveTo(w * 0.28, h * 0.2, w * 0.34, h * 0.85, w * 0.44, h * 0.5);
    ctx.bezierCurveTo(w * 0.5, h * 0.25, w * 0.55, h * 0.8, w * 0.62, h * 0.5);
    ctx.bezierCurveTo(w * 0.7, h * 0.2, w * 0.78, h * 0.7, w * 0.86, h * 0.45);
    ctx.stroke();
    setHasSignature(true);
  };
  const isComplete = method === 'draw' ? hasSignature : typedName.trim().length > 1;
  const handleSign = () => {
    if (!isComplete) return;
    setShowConfirmation(true);
    window.setTimeout(() => {
      setShowConfirmation(false);
      onNext();
    }, 1400);
  };
  return <div className="h-full flex flex-col overflow-hidden relative" data-id="element-197">
      <div className="flex-none" data-id="element-198">
        <Header title="Sign Your Agreement" onBack={onBack} data-id="element-199" />
        <ProgressIndicator currentStep={2} data-id="element-200" />
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-2" data-id="element-201">
        {/* Method toggle */}
        <div className="flex items-center justify-between mb-3" data-id="element-202">
          <h3 className="text-sm font-semibold text-gray-700" data-id="element-203">
            Choose a signature method
          </h3>
          {method === 'draw' && hasSignature && <button onClick={clearCanvas} className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#0F4C3A] transition-colors" data-id="element-204">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-205" />
              Clear
            </button>}
        </div>

        <div className="flex gap-2 mb-4" data-id="element-206">
          {([{
          id: 'draw',
          label: 'Draw',
          icon: Pencil
        }, {
          id: 'type',
          label: 'Type',
          icon: Type
        }] as const).map(m => {
          const active = method === m.id;
          return <button key={m.id} onClick={() => setMethod(m.id)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors ${active ? 'border-[#0F4C3A] bg-[#0F4C3A]/5 text-[#0F4C3A]' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'}`} data-id="element-207">
                <m.icon className="w-4 h-4" data-id="element-208" />
                {m.label}
              </button>;
        })}
        </div>

        {/* Signature area */}
        <div className={`relative rounded-2xl border-2 border-dashed h-52 mb-3 overflow-hidden transition-colors ${isComplete ? 'border-[#0F4C3A]/40 bg-[#0F4C3A]/[0.03]' : 'border-gray-300 bg-white'}`} data-id="element-209">
          {method === 'draw' ? <>
              <canvas ref={canvasRef} onPointerDown={startDraw} onPointerMove={moveDraw} onPointerUp={endDraw} onPointerLeave={endDraw} className="absolute inset-0 w-full h-full touch-none cursor-crosshair" data-id="element-210" />
              {!hasSignature && <button onClick={autoSign} className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-211">
                  <Pencil className="w-6 h-6 text-gray-300 mb-2" data-id="element-212" />
                  <span className="text-sm text-gray-400" data-id="element-213">
                    Sign here with your finger
                  </span>
                  <span className="text-[11px] text-gray-300 mt-1" data-id="element-214">
                    (or tap to auto-sign)
                  </span>
                </button>}
              <div className="absolute bottom-6 left-8 right-8 h-px bg-gray-200 pointer-events-none" data-id="element-215" />
            </> : <div className="absolute inset-0 flex flex-col items-center justify-center px-6" data-id="element-216">
              <input type="text" value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="Type your full name" className="w-full text-center text-3xl text-[#3B2F63] bg-transparent outline-none placeholder:text-gray-300" style={{
            fontFamily: 'cursive'
          }} data-id="element-217" />
              <div className="mt-2 w-3/4 h-px bg-gray-200" data-id="element-218" />
            </div>}
        </div>

        <div className="text-center mb-4" data-id="element-219">
          <button className="text-sm text-gray-700 font-medium underline underline-offset-4 hover:text-[#0F4C3A] transition-colors" data-id="element-220">
            View terms &amp; conditions
          </button>
        </div>

        {/* Security note */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500 bg-[#0F4C3A]/5 rounded-lg py-2.5 px-3" data-id="element-221">
          <ShieldCheck className="w-4 h-4 text-[#0F4C3A] flex-shrink-0" data-id="element-222" />
          <span data-id="element-223">
            Secured with{' '}
            <span className="font-semibold text-gray-700" data-id="element-224">
              256-bit encryption
            </span>{' '}
            · Legally binding e-signature
          </span>
        </div>
      </div>

      {/* Footer Button */}
      <div className="flex-none p-6 pt-2 pb-8 bg-[#F7F8F7]" data-id="element-225">
        <motion.button whileTap={{
        scale: isComplete ? 0.97 : 1
      }} onClick={handleSign} disabled={!isComplete || showConfirmation} className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-lg transition-colors shadow-md ${isComplete ? 'bg-[#0F4C3A] text-white hover:bg-[#134E3A]' : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'}`} data-id="element-226">
          <Lock className="w-4 h-4" data-id="element-227" />
          Sign &amp; Continue
        </motion.button>
      </div>

      {/* Transient confirmation */}
      <AnimatePresence data-id="element-228">
        {showConfirmation && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[1px]" data-id="element-229">
            <motion.div initial={{
          scale: 0.85,
          y: 8,
          opacity: 0
        }} animate={{
          scale: 1,
          y: 0,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 24
        }} role="status" aria-live="polite" className="bg-white rounded-2xl px-8 py-7 shadow-2xl flex flex-col items-center mx-8" data-id="element-230">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 360,
            damping: 18,
            delay: 0.1
          }} className="w-16 h-16 bg-[#0F4C3A] rounded-full flex items-center justify-center mb-4 shadow-lg" data-id="element-231">
                <Check className="w-8 h-8 text-white" strokeWidth={3} data-id="element-232" />
              </motion.div>
              <p className="text-base font-bold text-gray-900" data-id="element-233">
                Signature saved
              </p>
              <p className="text-xs text-gray-500 mt-1 text-center" data-id="element-234">
                Your agreement has been signed securely.
              </p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}